import { useState, useCallback } from 'react';
import { FolderData } from '../types';
import { useCurrentOrg } from '~/src/entities/user/hooks/useCurrentOrg';
import clioDocumentPickerService from '~/src/services/api/clioDocumentPickerService';
import { transformFoldersToFolderData } from './helpers/hookHelpers';

const PAGINATION_LIMIT = 25;

/**
 * Hook for managing individual folder state and its direct children with pagination
 * Each FolderRow uses this hook independently for maximum isolation
 */
export const useFolderHierarchy = (folder: FolderData) => {
  // This folder's direct children
  const [children, setChildren] = useState<FolderData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedChildren, setHasLoadedChildren] = useState(false);
  const [hasMoreChildren, setHasMoreChildren] = useState(false);
  const [nextPageToken, setNextPageToken] = useState<string | undefined | null>(
    undefined,
  );
  const [hasLoadingError, setHasLoadingError] = useState(false);
  const { org } = useCurrentOrg();

  const loadChildren = useCallback(
    async (currentLevel: number): Promise<void> => {
      // Prevent concurrent calls and duplicate initial loads
      if (
        isLoading ||
        hasLoadingError ||
        (hasLoadedChildren && !nextPageToken)
      ) {
        return;
      }

      setIsLoading(true);

      try {
        if (!org?.fprint) {
          throw new Error('Organization fprint is not set');
        }

        const apiResponse = await clioDocumentPickerService.listFolders(
          org.fprint,
          {
            parent_id: folder.id,
            limit: PAGINATION_LIMIT,
            page_token: nextPageToken || undefined,
          },
        );

        let newChildren: FolderData[] = [];
        if (apiResponse.folders) {
          newChildren = transformFoldersToFolderData(
            apiResponse.folders,
            currentLevel + 1,
            folder.id,
          );
        }

        setChildren((currentChildren) => [...currentChildren, ...newChildren]);

        if (apiResponse.next_page_token) {
          setHasMoreChildren(true);
          setNextPageToken(apiResponse.next_page_token);
        } else {
          setHasMoreChildren(false);
          setNextPageToken(null);
        }
      } catch (error) {
        console.error('Failed to load folder children:', error);
        setHasLoadingError(true);
      } finally {
        setIsLoading(false);
        if (!hasLoadedChildren) {
          setHasLoadedChildren(true);
        }
      }
    },
    [
      folder.id,
      hasLoadedChildren,
      isLoading,
      org?.fprint,
      nextPageToken,
      hasLoadingError,
    ],
  );

  return {
    children,
    isLoading,
    hasLoadedChildren,
    hasMoreChildren,
    hasLoadingError,
    loadChildren,
  };
};
